import React, { useState, useRef } from 'react';
import { PRODUCTS } from '../constants';
import { Category, Product } from '../types';
import { ShoppingCart, Plus, Minus, Heart, Eye } from 'lucide-react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ProductQuickViewModal } from './ProductQuickViewModal';

gsap.registerPlugin(ScrollTrigger);

const CATEGORIES: Category[] = [
  'All',
  'Pain & Mobility',
  'Special Care',
  'Digestive & Metabolic',
  'Skin & Immunity',
  'Heart & Circulation',
  'Sleep & Calm',
  'Women\'s Wellness',
  'Liver & Detox',
  'Vitality & Energy'
];

interface ProductSectionProps {
  onProductSelect: (product: Product) => void;
  onAddToCart: (product: Product, quantity: number) => void;
  onToggleWishlist: (product: Product) => void;
  wishlist: string[]; // array of product IDs
}

export const ProductSection: React.FC<ProductSectionProps> = ({ 
  onProductSelect, 
  onAddToCart, 
  onToggleWishlist,
  wishlist 
}) => {
  const [activeCategory, setActiveCategory] = useState<Category>('All');
  const [quickViewProduct, setQuickViewProduct] = useState<Product | null>(null);
  const container = useRef<HTMLElement>(null);

  const filteredProducts = activeCategory === 'All' 
    ? PRODUCTS 
    : PRODUCTS.filter(p => p.category === activeCategory);

  useGSAP(() => {
    gsap.fromTo('.product-card', 
      { y: 30, opacity: 0 },
      { 
        y: 0, 
        opacity: 1, 
        stagger: 0.05, 
        duration: 0.5, 
        ease: 'power2.out',
        scrollTrigger: {
          trigger: '.product-grid',
          start: 'top 90%',
        }
      }
    );
  }, { dependencies: [activeCategory], scope: container });

  return (
    <section ref={container} id="products" className="py-20 bg-earth-100">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-herbal-900 mb-4">
            Curated Herbal Solutions
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Explore our range of premium Indonesian Jamu remedies, tailored for your specific health needs.
          </p>
        </div>

        {/* Categories Scroller */}
        <div className="flex overflow-x-auto pb-4 mb-10 gap-3 no-scrollbar justify-start md:justify-center">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`whitespace-nowrap px-6 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                activeCategory === cat
                  ? 'bg-herbal-800 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-herbal-100'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        <div className="product-grid grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-8">
          {filteredProducts.map((product) => (
            <div key={product.id} className="product-card">
              <ProductCard 
                product={product} 
                onSelect={() => onProductSelect(product)} 
                onAddToCart={onAddToCart}
                onToggleWishlist={() => onToggleWishlist(product)}
                onQuickView={() => setQuickViewProduct(product)}
                isWishlisted={wishlist.includes(product.id)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Quick View Modal */}
      <ProductQuickViewModal 
        isOpen={!!quickViewProduct}
        onClose={() => setQuickViewProduct(null)}
        product={quickViewProduct}
        onAddToCart={onAddToCart}
        onViewDetails={onProductSelect}
      />
    </section>
  );
};

interface ProductCardProps {
  product: Product;
  onSelect: () => void;
  onAddToCart: (p: Product, q: number) => void;
  onToggleWishlist: () => void;
  onQuickView: () => void;
  isWishlisted: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({ 
  product, 
  onSelect, 
  onAddToCart, 
  onToggleWishlist,
  onQuickView,
  isWishlisted 
}) => {
  const [quantity, setQuantity] = useState(1);
  const price = product.price || 0;
  const totalPrice = price * quantity;
  const formattedPrice = new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(totalPrice);

  const handleIncrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    setQuantity(prev => prev + 1);
  };

  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    setQuantity(prev => (prev > 1 ? prev - 1 : 1));
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddToCart(product, quantity);
    setQuantity(1);
  };

  const handleHeartClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleWishlist();
  };
  
  const handleQuickViewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onQuickView();
  };

  return (
    <div className="bg-white rounded-xl md:rounded-2xl shadow-sm hover:shadow-xl transition-shadow duration-300 overflow-hidden flex flex-col h-full border border-gray-100 group">
      <div className="relative h-40 md:h-72 overflow-hidden bg-white border-b border-gray-50 cursor-pointer" onClick={onSelect}>
        <img 
          src={product.image} 
          alt={product.name} 
          className="w-full h-full object-contain p-2 md:p-6 transform group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-2 left-2 md:top-4 md:left-4">
          <span className="bg-herbal-100/90 backdrop-blur-sm text-herbal-800 text-[10px] md:text-xs font-bold px-2 py-0.5 md:px-3 md:py-1 rounded-full shadow-sm">
            {product.category}
          </span>
        </div>
        
        {/* Wishlist Heart */}
        <button 
          onClick={handleHeartClick}
          className={`absolute top-2 right-2 md:top-4 md:right-4 p-2 rounded-full backdrop-blur-md transition-all shadow-sm ${
            isWishlisted 
              ? 'bg-turmeric-500 text-white' 
              : 'bg-white/80 text-gray-400 hover:text-turmeric-500'
          }`}
          title="Add to Wishlist"
        >
          <Heart className={`h-4 w-4 md:h-5 md:w-5 ${isWishlisted ? 'fill-current' : ''}`} />
        </button>
      </div>
      
      <div className="p-3 md:p-6 flex flex-col flex-grow">
        <div className="flex justify-between items-start mb-1 md:mb-2">
          <h3 
            className="font-heading text-sm md:text-xl font-bold text-gray-900 group-hover:text-herbal-800 transition-colors cursor-pointer line-clamp-2 md:line-clamp-none"
            onClick={onSelect}
          >
            {product.name}
          </h3>
        </div>
        
        <p className="text-[10px] md:text-sm font-semibold text-turmeric-600 mb-2 md:mb-3 uppercase tracking-wide md:text-xs line-clamp-1">
          {product.purpose}
        </p>
        
        <p className="text-gray-600 text-xs md:text-sm leading-relaxed mb-3 md:mb-6 flex-grow line-clamp-2 md:line-clamp-none">
          {product.description}
        </p>

        {/* Price & Cart Controls */}
        <div className="mt-auto pt-3 border-t border-gray-100">
           <div className="flex items-center justify-between mb-3">
              <span className="text-sm md:text-lg font-bold text-herbal-900">{formattedPrice}</span>
              
              {/* Mini Quantity Selector */}
              <div className="flex items-center bg-gray-50 rounded-lg border border-gray-200">
                 <button onClick={handleDecrement} className="p-1 hover:text-herbal-800 text-gray-500 disabled:opacity-50">
                    <Minus className="h-3 w-3 md:h-4 md:w-4" />
                 </button>
                 <span className="text-xs md:text-sm font-bold w-4 md:w-6 text-center">{quantity}</span>
                 <button onClick={handleIncrement} className="p-1 hover:text-herbal-800 text-gray-500">
                    <Plus className="h-3 w-3 md:h-4 md:w-4" />
                 </button>
              </div>
           </div>

          <div className="flex items-center gap-2">
            {/* Quick View Button - Always visible now */}
            <button 
              onClick={handleQuickViewClick}
              className="flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-600 h-9 w-9 md:h-10 md:w-10 rounded-lg transition-colors"
              title="Quick View"
            >
              <Eye className="h-4 w-4 md:h-5 md:w-5" />
            </button>

            <button 
              onClick={onSelect}
              className="text-gray-500 hover:text-herbal-800 font-semibold text-xs md:text-sm flex items-center transition-colors flex-grow justify-center lg:justify-start"
            >
              View Details
            </button>
            
            <button 
              onClick={handleAddToCart}
              className="bg-herbal-800 hover:bg-herbal-900 text-white h-9 w-9 md:h-10 md:w-10 rounded-lg flex items-center justify-center transition-colors shadow-sm flex-shrink-0" 
              aria-label="Add to cart"
            >
              <ShoppingCart className="h-4 w-4 md:h-5 md:w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};